import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { 
  SiEthereum, 
  SiBlockchaindotcom, 
  SiSolidity, 
  SiIpfs,
  SiBinance,
  SiSolana,
  SiBitcoin
} from 'react-icons/si';
import { GiCubes } from 'react-icons/gi';
import { SUI } from '../assets/icons/sui';

const glowAnimation = (color) => keyframes`
  0% { 
    box-shadow: 0 0 5px ${color}33, 
                0 0 25px ${color}33, 
                0 0 50px ${color}33; 
  }
  50% { 
    box-shadow: 0 0 10px ${color}66, 
                0 0 50px ${color}66, 
                0 0 75px ${color}66; 
  }
  100% { 
    box-shadow: 0 0 5px ${color}33, 
                0 0 25px ${color}33, 
                0 0 50px ${color}33; 
  }
`;

const hexagonPattern = keyframes`
  0% { background-position: 0 0; }
  100% { background-position: 100px 100px; }
`;

const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-20px); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
`;

const meteorAnimation = keyframes`
  0% {
    transform: rotate(215deg) translateX(0);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  70% {
    opacity: 1;
  }
  100% {
    transform: rotate(215deg) translateX(-900px);
    opacity: 0;
  }
`;

const BannerContainer = styled.section`
  padding: 4rem 1rem;
  position: relative;
  overflow: hidden;
  min-height: 70vh;
  background: #0f172a;
//   background: linear-gradient(
//     -45deg, 
//     #0f172a,
//     #1e293b,
//     #1e1b4b,
//     #312e81
//   );
  background-size: 400% 400%;
  animation: ${gradientMove} 15s ease infinite;
  
  @media (min-width: 768px) {
    padding: 8rem 2rem;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  gap: 2rem;
  align-items: center;
  padding: 0 1rem;
  
  @media (max-width: 1200px) {
    flex-direction: column;
    gap: 4rem;
  }
`;

const BlockchainVisual = styled.div`
  flex: 1;
  position: relative;
  height: 400px;
  z-index: 2;
  display: none;
  
  @media (min-width: 1200px) {
    display: block;
    height: 600px;
  }
`;

const blockchainIcons = [
  { 
    icon: <SiEthereum />, 
    color: '#627EEA'  // Ethereum blue
  },
  { 
    icon: <SiBinance />, 
    color: '#F3BA2F'  // Binance yellow
  },
  { 
    icon: <SiSolana />, 
    color: '#14F195'  // Solana green
  },
  { 
    icon: <SiBitcoin />, 
    color: '#F7931A'  // Bitcoin orange
  },
  { 
    icon: <SUI />, 
    color: '#6fbcf0'  // Sui blue
  }
];

const BlockchainNode = styled(motion.div)`
  width: 100px;
  height: 100px;
  background: ${props => `rgba(${props.rgbColor}, 0.1)`};
  backdrop-filter: blur(5px);
  border: 2px solid ${props => `rgba(${props.rgbColor}, 0.3)`};
  border-radius: 20px;
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${props => glowAnimation(props.hexColor)} 3s infinite;

  svg {
    font-size: 2.5rem;
    color: ${props => props.hexColor};
  }

  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    background: ${props => `linear-gradient(45deg, ${props.hexColor}, ${props.hexColor}88)`};
    border-radius: 22px;
    z-index: -1;
    opacity: 0.5;
  }
`;

const ConnectionLine = styled(motion.div)`
  position: absolute;
  height: 2px;
  background: linear-gradient(90deg, 
    rgba(99, 102, 241, 0.8), 
    rgba(139, 92, 246, 0.8)
  );
  transform-origin: left center;
  filter: drop-shadow(0 0 8px rgba(99, 102, 241, 0.5));

  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: inherit;
    filter: blur(4px);
  }
`;

const ContentLeft = styled.div`
  flex: 1;

  h1 {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-align: center;
    
    @media (min-width: 768px) {
      font-size: 3.5rem;
      text-align: left;
    }
  }

  p {
    font-size: 1rem;
    color: #94a3b8;
    line-height: 1.7;
    margin-bottom: 2rem;
    text-align: center;
    
    @media (min-width: 768px) {
      font-size: 1.2rem;
      text-align: left;
    }
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
`;

const FeatureItem = styled(motion.div)`
  background: rgba(99, 102, 241, 0.1);
  padding: 1.25rem;
  border-radius: 12px;
  border: 1px solid rgba(99, 102, 241, 0.2);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    background: rgba(99, 102, 241, 0.15);
  }

  svg {
    font-size: 1.25rem;
    color: #6366f1;
    
    @media (min-width: 768px) {
      font-size: 1.5rem;
    }
  }

  h3 {
    font-size: 0.9rem;
    color: #e2e8f0;
    margin: 0;
    
    @media (min-width: 768px) {
      font-size: 1rem;
    }
  }
`;

const CodeBlock = styled(motion.div)`
  background: rgba(30, 27, 75, 0.9);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 16px;
  padding: 1.5rem;
  font-family: 'Fira Code', monospace;
  font-size: 0.9rem;
  color: #e2e8f0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #6366f1, #8b5cf6);
  }

  pre {
    margin: 0;
    code {
      .keyword { color: #93c5fd; }
      .string { color: #4ade80; }
      .comment { color: #64748b; }
    }
  }
`;

const GridOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: linear-gradient(rgba(99, 102, 241, 0.1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(99, 102, 241, 0.1) 1px, transparent 1px);
  background-size: 50px 50px;
  opacity: 0.3;
`;

const FloatingOrb = styled.div`
  display: none;
  
  @media (min-width: 768px) {
    display: block;
    position: absolute;
    width: ${props => props.size}px;
    height: ${props => props.size}px;
    background: radial-gradient(circle at 30% 30%, ${props => props.color}, transparent);
    border-radius: 50%;
    opacity: 0.3;
    animation: ${float} 6s ease-in-out infinite;
    animation-delay: ${props => props.delay}s;
    filter: blur(10px);
  }
`;

const ParticleEffect = styled(motion.div)`
  position: absolute;
  width: 4px;
  height: 4px;
  background: rgba(99, 102, 241, 0.5);
  border-radius: 50%;
  filter: blur(1px);
`;

const MeteorShower = styled.div`
  display: none;
  
  @media (min-width: 768px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 1;
    overflow: hidden;
  }
`;

const Meteor = styled.div`
  position: absolute;
  width: ${props => props.size || 2}px;
  height: ${props => props.size * 50 || 100}px;
  background: linear-gradient(
    45deg, 
    ${props => props.color || 'rgba(99, 102, 241, 1)'} 0%, 
    rgba(99, 102, 241, 0.1) 50%,
    transparent 100%
  );
  border-radius: 999px;
  filter: drop-shadow(0 0 6px ${props => props.color || 'rgba(99, 102, 241, 0.8)'});
  animation: ${meteorAnimation} ${props => props.duration || 1}s linear infinite;
  animation-delay: ${props => props.delay || 0}s;
  top: ${props => props.top || 0}%;
  left: ${props => props.left || 0}%;
  opacity: 0;
  transform-origin: ${props => props.origin || '50% 50%'};
  
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    transform: translateX(-50%);
    background: inherit;
    filter: blur(20px);
  }
`;

const meteorColors = [
  'rgba(99, 102, 241, 1)',   // Indigo
  'rgba(139, 92, 246, 1)',   // Purple
  'rgba(67, 56, 202, 1)',    // Deep Blue
  'rgba(79, 70, 229, 1)',    // Royal Blue
  'rgba(124, 58, 237, 1)'    // Violet
];

// Define fixed positions for the nodes
const nodePositions = [
  { top: '20%', left: '20%' },
  { top: '25%', left: '63%' },
  { top: '54%', left: '42%' },
  { top: '80%', left: '20%' },
  { top: '92%', left: '60%' }
];

const Web3Banner = () => {
  return (
    <BannerContainer>
      <MeteorShower>
        {/* First layer - larger, slower meteors */}
        {Array.from({ length: 8 }).map((_, i) => (
          <Meteor
            key={`large-${i}`}
            size={Math.random() * 3 + 2}
            duration={Math.random() * 2 + 2}
            delay={Math.random() * 3}
            top={Math.random() * 40}
            left={Math.random() * 100 + 50}
            color={meteorColors[Math.floor(Math.random() * meteorColors.length)]}
            origin={`${Math.random() * 100}% ${Math.random() * 100}%`}
          />
        ))}
        
        {/* Second layer - medium meteors */}
        {Array.from({ length: 12 }).map((_, i) => (
          <Meteor
            key={`medium-${i}`}
            size={Math.random() * 2 + 1}
            duration={Math.random() * 1.5 + 1}
            delay={Math.random() * 4}
            top={Math.random() * 50}
            left={Math.random() * 100 + 40}
            color={meteorColors[Math.floor(Math.random() * meteorColors.length)]}
            origin={`${Math.random() * 100}% ${Math.random() * 100}%`}
          />
        ))}
        
        {/* Third layer - small, fast meteors */}
        {Array.from({ length: 15 }).map((_, i) => (
          <Meteor
            key={`small-${i}`}
            size={Math.random() * 1 + 0.5}
            duration={Math.random() * 1 + 0.8}
            delay={Math.random() * 5}
            top={Math.random() * 60}
            left={Math.random() * 100 + 30}
            color={meteorColors[Math.floor(Math.random() * meteorColors.length)]}
            origin={`${Math.random() * 100}% ${Math.random() * 100}%`}
          />
        ))}
      </MeteorShower>

      <FloatingOrb size={300} color="rgba(99, 102, 241, 0.2)" delay={0} style={{ top: '20%', left: '10%' }} />
      <FloatingOrb size={200} color="rgba(139, 92, 246, 0.2)" delay={1} style={{ top: '60%', right: '15%' }} />
      <FloatingOrb size={250} color="rgba(67, 56, 202, 0.2)" delay={2} style={{ bottom: '10%', left: '30%' }} />
      
      <GridOverlay />
      
      {Array.from({ length: 20 }).map((_, i) => (
        <ParticleEffect
          key={i}
          initial={{ 
            x: Math.random() * window.innerWidth,
            y: Math.random() * window.innerHeight,
            opacity: Math.random() 
          }}
          animate={{ 
            y: [0, -100, 0],
            opacity: [0, 1, 0]
          }}
          transition={{
            duration: Math.random() * 5 + 5,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      ))}

      <ContentWrapper>
        <ContentLeft>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Web3 Development Services
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Morpheus Tech works on own projects and also provides services to clients. 
          </motion.p>
          <FeatureGrid>
            {[
              { icon: <SiSolidity />, title: "AI Agent Framework Integration" },
              { icon: <SiBlockchaindotcom />, title: "DeFi Protocols" },
              { icon: <SiIpfs />, title: "GameFi development" },
              { icon: <GiCubes />, title: "NFT" }
            ].map((feature, index) => (
              <FeatureItem
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
              >
                {feature.icon}
                <h3>{feature.title}</h3>
              </FeatureItem>
            ))}
          </FeatureGrid>
        </ContentLeft>
        
        <BlockchainVisual>
          {blockchainIcons.map((blockchain, i) => (
            <React.Fragment key={i}>
              <BlockchainNode
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ 
                  delay: i * 0.2,
                  type: "spring",
                  stiffness: 100
                }}
                style={nodePositions[i]}
                hexColor={blockchain.color}
                rgbColor={blockchain.color.replace('#', '').match(/.{2}/g)
                  .map(hex => parseInt(hex, 16)).join(', ')}
              >
                {blockchain.icon}
              </BlockchainNode>

              {/* Add connection lines between nodes */}
              {/* {i < blockchainIcons.length - 1 && (
                <ConnectionLine
                  initial={{ opacity: 0, scaleX: 0 }}
                  animate={{ opacity: 1, scaleX: 1 }}
                  transition={{ delay: (i + 1) * 0.2, duration: 0.5 }}
                  style={{
                    top: `calc(${nodePositions[i].top} + 50px)`,
                    left: `calc(${nodePositions[i].left} + 50px)`,
                    width: '150px',
                    transform: `rotate(${Math.atan2(
                      parseInt(nodePositions[i + 1].top) - parseInt(nodePositions[i].top),
                      parseInt(nodePositions[i + 1].left) - parseInt(nodePositions[i].left)
                    )}rad)`
                  }}
                />
              )} */}
            </React.Fragment>
          ))}
        </BlockchainVisual>
      </ContentWrapper>
    </BannerContainer>
  );
};

export default Web3Banner;